import type { ChangeEvent, RefObject } from 'react';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw,
  Link2,
  Plug,
  PlugZap,
  FileSpreadsheet,
} from 'lucide-react';
import { MiroIcon, JiraIcon } from './IntegrationIcons';
import type { SyncStatus } from './integrationTypes';

export function IntegrationStrip({
  miroConnected,
  jiraConnected,
  hideExports,
  syncStatus,
  csvExcelInputRef,
  onCsvImportClick,
  onCsvTemplateClick,
  handleExcelCsvFileSelected,
  handleDownloadImportTemplateCsv,
  handleDownloadImportTemplateExcel,
  handleExportCsv,
  handleExportExcel,
  setShowMiroConfig,
  setShowImportMiro,
  setShowExportMiro,
  setShowJiraConfig,
  setShowExportJira,
  setSelectedPriorities,
}: {
  miroConnected: boolean;
  jiraConnected: boolean;
  hideExports: boolean;
  syncStatus: SyncStatus;
  csvExcelInputRef: RefObject<HTMLInputElement>;
  onCsvImportClick?: () => void;
  onCsvTemplateClick?: () => void;
  handleExcelCsvFileSelected: (e: ChangeEvent<HTMLInputElement>) => void;
  handleDownloadImportTemplateCsv: () => void;
  handleDownloadImportTemplateExcel: () => void;
  handleExportCsv: () => void;
  handleExportExcel: () => void;
  setShowMiroConfig: (show: boolean) => void;
  setShowImportMiro: (show: boolean) => void;
  setShowExportMiro: (show: boolean) => void;
  setShowJiraConfig: (show: boolean) => void;
  setShowExportJira: (show: boolean) => void;
  setSelectedPriorities: (ids: string[]) => void;
}) {
  // Format relative time
  const formatRelativeTime = (date?: Date) => {
    if (!date) return 'Never';
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  return (
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2">
        {/* Hidden file input for Excel/CSV import */}
        <input
          ref={csvExcelInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={handleExcelCsvFileSelected}
        />

        <div className="flex flex-wrap items-center gap-2">
          {/* Label */}
          <span className="flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] mr-1 shrink-0">
            <RefreshCw className="h-3.5 w-3.5" />
            Integration Sync
          </span>

          <span className="text-[var(--text-ter)] dark:text-[var(--rule-bright)] hidden sm:block">|</span>

          {/* Miro */}
          <div className="flex items-center gap-1.5">
            <span className={miroConnected ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}>
              <MiroIcon />
            </span>
            {miroConnected ? (
              <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                <PlugZap className="h-3 w-3" />
                <span className="hidden sm:inline">Miro</span>
              </span>
            ) : (
              <button
                onClick={() => setShowMiroConfig(true)}
                className="flex items-center gap-1 text-xs text-[var(--mustard)] hover:text-[var(--mustard)] font-medium"
              >
                <Plug className="h-3 w-3" />
                <span className="hidden sm:inline">Connect</span>
              </button>
            )}
            <button
              onClick={() => miroConnected ? setShowImportMiro(true) : setShowMiroConfig(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
            >
              <ArrowDownToLine className="h-3 w-3" />
              Import from Miro
            </button>
            {!hideExports && (
              <button
                onClick={() => miroConnected ? (setSelectedPriorities([]), setShowExportMiro(true)) : setShowMiroConfig(true)}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
              >
                <ArrowUpFromLine className="h-3 w-3" />
                Export
              </button>
            )}
          </div>

          <span className="text-[var(--text-ter)] dark:text-[var(--rule-bright)] hidden sm:block">|</span>

          {/* Jira */}
          <div className="flex items-center gap-1.5">
            <span className="text-[var(--text-ter)]"><JiraIcon /></span>
            {jiraConnected ? (
              <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                <PlugZap className="h-3 w-3" />
                <span className="hidden sm:inline">Jira</span>
              </span>
            ) : (
              <button
                onClick={() => setShowJiraConfig(true)}
                className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
              >
                <Plug className="h-3 w-3" />
                <span className="hidden sm:inline">Connect</span>
              </button>
            )}
            {!hideExports && (
              <button
                onClick={() => jiraConnected ? (setSelectedPriorities([]), setShowExportJira(true)) : setShowJiraConfig(true)}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
              >
                <ArrowUpFromLine className="h-3 w-3" />
                Export
              </button>
            )}
          </div>

          <span className="text-[var(--text-ter)] dark:text-[var(--rule-bright)] hidden sm:block">|</span>

          {/* Excel / CSV */}
          <div className="flex items-center gap-1.5">
            <FileSpreadsheet className="h-3.5 w-3.5 text-[var(--green)]" />
            <button
              onClick={onCsvImportClick ?? (() => csvExcelInputRef.current?.click())}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-[var(--green)] bg-[var(--bg-raised)] hover:bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--surface)] dark:border-[var(--green)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--green)]/20"
            >
              <ArrowDownToLine className="h-3 w-3" />
              Import from Excel/CSV
            </button>
            <button
              onClick={onCsvTemplateClick ?? handleDownloadImportTemplateCsv}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
              title="Download a blank import template with all columns"
            >
              <ArrowUpFromLine className="h-3 w-3" />
              Template (CSV)
            </button>
            <button
              onClick={onCsvTemplateClick ?? handleDownloadImportTemplateExcel}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
              title="Download a blank import template with all columns"
            >
              <ArrowUpFromLine className="h-3 w-3" />
              Template (Excel)
            </button>
            {!hideExports && (
              <>
                <button
                  onClick={handleExportCsv}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
                >
                  <ArrowUpFromLine className="h-3 w-3" />
                  CSV
                </button>
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
                >
                  <ArrowUpFromLine className="h-3 w-3" />
                  Excel
                </button>
              </>
            )}
          </div>

          {/* Last synced + linked count — right end */}
          {(syncStatus.lastMiroSync || syncStatus.lastJiraSync) && (
            <span className="ml-auto flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
              <RefreshCw className="h-3 w-3" />
              Synced {formatRelativeTime(
                [syncStatus.lastMiroSync, syncStatus.lastJiraSync]
                  .filter((d): d is Date => !!d)
                  .sort((a, b) => b.getTime() - a.getTime())[0]
              )}
            </span>
          )}
          {syncStatus.linkedItems > 0 && (
            <span className={`${(syncStatus.lastMiroSync || syncStatus.lastJiraSync) ? '' : 'ml-auto '}flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]`}>
              <Link2 className="h-3 w-3" />
              {syncStatus.linkedItems} linked
            </span>
          )}
        </div>
      </div>
  );
}
